/**
 * Text-to-Speech Context
 *
 * Global state management for text-to-speech functionality.
 * Allows any component to trigger or control read-aloud features.
 */

import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'

// High-quality voice names to prefer (Google, Microsoft, Apple voices)
const QUALITY_VOICE_PATTERNS = [
  /^Google/i,
  /^Microsoft/i,
  /^Samantha/i, // macOS
  /^Alex/i, // macOS
  /^Karen/i, // macOS
  /^Victoria/i, // macOS
  /^Daniel/i, // macOS
  /^Moira/i, // macOS
  /^Tessa/i, // macOS
  /^Fiona/i, // macOS
]

interface TextToSpeechContextType {
  // State
  isPlaying: boolean
  isPaused: boolean
  rate: number
  selectedVoice: string
  voices: SpeechSynthesisVoice[]
  currentText: string

  // Actions
  speak: (text: string) => void
  pause: () => void
  resume: () => void
  stop: () => void
  setRate: (rate: number) => void
  setVoice: (voiceName: string) => void

  // Helpers
  isSupported: boolean
  getQualityVoices: () => SpeechSynthesisVoice[]
}

const TextToSpeechContext = createContext<TextToSpeechContextType | undefined>(undefined)

// Maximum characters per utterance chunk (conservative to work across browsers)
const MAX_UTTERANCE_LENGTH = 150

export function TextToSpeechProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [rate, setRateState] = useState(1.0)
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [selectedVoice, setSelectedVoice] = useState<string>('')
  const [currentText, setCurrentText] = useState<string>('')
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)
  const textQueueRef = useRef<string[]>([])
  const keepAliveIntervalRef = useRef<number | null>(null)
  const isSupported = 'speechSynthesis' in window

  // Load available voices
  useEffect(() => {
    if (!isSupported) return

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices()
      setVoices(availableVoices)

      // Set default to first quality English voice
      if (availableVoices.length > 0 && !selectedVoice) {
        const qualityVoices = getQualityVoicesFromList(availableVoices)
        const defaultVoice =
          qualityVoices[0] || availableVoices.find((v) => v.lang.startsWith('en'))
        if (defaultVoice) {
          setSelectedVoice(defaultVoice.name)
        }
      }
    }

    loadVoices()

    // Chrome loads voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices
    }

    return () => {
      window.speechSynthesis.cancel()
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current)
      }
    }
  }, [isSupported])

  // Test if a voice actually works by attempting to speak with it
  const testVoice = async (voice: SpeechSynthesisVoice): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        const testUtterance = new SpeechSynthesisUtterance('')
        testUtterance.voice = voice
        testUtterance.volume = 0 // Silent test

        let resolved = false
        const timeout = setTimeout(() => {
          if (!resolved) {
            resolved = true
            resolve(false) // Voice failed test
          }
        }, 500) // 500ms timeout

        testUtterance.onstart = () => {
          if (!resolved) {
            resolved = true
            clearTimeout(timeout)
            window.speechSynthesis.cancel()
            resolve(true) // Voice works
          }
        }

        testUtterance.onerror = () => {
          if (!resolved) {
            resolved = true
            clearTimeout(timeout)
            resolve(false) // Voice failed
          }
        }

        // Some voices fail silently without onerror, so we rely on timeout
        window.speechSynthesis.speak(testUtterance)

        // Clean up immediately for faster testing
        setTimeout(() => window.speechSynthesis.cancel(), 50)
      } catch (error) {
        resolve(false) // Voice failed to initialize
      }
    })
  }

  const getQualityVoicesFromList = (voiceList: SpeechSynthesisVoice[]) => {
    return voiceList
      .filter((voice) => voice.lang.startsWith('en'))
      .filter((voice) => {
        // Include if matches any quality pattern
        return QUALITY_VOICE_PATTERNS.some((pattern) => pattern.test(voice.name))
      })
      .sort((a, b) => {
        // Prioritize by pattern order
        const aIndex = QUALITY_VOICE_PATTERNS.findIndex((p) => p.test(a.name))
        const bIndex = QUALITY_VOICE_PATTERNS.findIndex((p) => p.test(b.name))
        return aIndex - bIndex
      })
  }

  const getQualityVoices = () => {
    const qualityVoices = getQualityVoicesFromList(voices)
    const voicesToReturn =
      qualityVoices.length > 0 ? qualityVoices : voices.filter((v) => v.lang.startsWith('en'))

    // Return at least one voice (default system voice) if none found
    if (voicesToReturn.length === 0 && voices.length > 0) {
      return [voices[0]]
    }

    return voicesToReturn
  }

  // Helper to chunk text into sentences at natural boundaries
  const chunkText = (text: string): string[] => {
    // If text is short enough, return as-is
    if (text.length <= MAX_UTTERANCE_LENGTH) {
      return [text]
    }

    const chunks: string[] = []

    // First try to split by sentences
    const sentences: string[] = text.match(/[^.!?\n]+[.!?\n]+/g) || []
    const remaining = text.replace(/[^.!?\n]+[.!?\n]+/g, '')
    if (remaining.trim()) {
      sentences.push(remaining)
    }

    for (const sentence of sentences) {
      // If sentence fits in max length, try to add to current chunk
      if (sentence.length <= MAX_UTTERANCE_LENGTH) {
        // Check if we can add to last chunk
        if (
          chunks.length > 0 &&
          chunks[chunks.length - 1].length + sentence.length <= MAX_UTTERANCE_LENGTH
        ) {
          chunks[chunks.length - 1] += sentence
        } else {
          chunks.push(sentence)
        }
      } else {
        // Sentence is too long, need to split by words
        const words = sentence.split(/\s+/)
        let currentChunk = ''

        for (const word of words) {
          // If adding this word would exceed limit
          if (currentChunk.length + word.length + 1 > MAX_UTTERANCE_LENGTH) {
            // Save current chunk if not empty
            if (currentChunk.trim()) {
              chunks.push(currentChunk.trim())
            }
            // Start new chunk with this word
            currentChunk = word
          } else {
            // Add word to current chunk
            currentChunk += (currentChunk ? ' ' : '') + word
          }
        }

        // Add remaining chunk
        if (currentChunk.trim()) {
          // Try to merge with last chunk if possible
          if (
            chunks.length > 0 &&
            chunks[chunks.length - 1].length + currentChunk.length + 1 <= MAX_UTTERANCE_LENGTH
          ) {
            chunks[chunks.length - 1] += ' ' + currentChunk.trim()
          } else {
            chunks.push(currentChunk.trim())
          }
        }
      }
    }

    console.log(
      `[TTS] Chunked text into ${chunks.length} parts:`,
      chunks.map((c, i) => `[${i}] ${c.length} chars: "${c.substring(0, 40)}..."`),
    )
    return chunks.filter((c) => c.trim().length > 0)
  }

  // Start keep-alive to prevent browser from timing out
  const startKeepAlive = () => {
    // Clear any existing interval
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current)
    }

    // Resume every 10 seconds to keep speech synthesis alive
    // This is a workaround for Chrome's 15-second timeout bug
    keepAliveIntervalRef.current = window.setInterval(() => {
      if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
        console.log('[TTS] Keep-alive: resuming to prevent timeout')
        window.speechSynthesis.pause()
        window.speechSynthesis.resume()
      }
    }, 10000)
  }

  // Stop keep-alive
  const stopKeepAlive = () => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current)
      keepAliveIntervalRef.current = null
    }
  }

  // Speak the next chunk in the queue
  const speakNextChunk = () => {
    if (textQueueRef.current.length === 0) {
      console.log('[TTS] Queue empty, stopping')
      stopKeepAlive()
      setIsPlaying(false)
      setIsPaused(false)
      setCurrentText('')
      return
    }

    const chunk = textQueueRef.current.shift()!
    console.log(`[TTS] Speaking chunk (${textQueueRef.current.length} remaining): "${chunk}"`)

    const utterance = new SpeechSynthesisUtterance(chunk)
    utterance.rate = rate
    utterance.volume = 1.0
    utterance.pitch = 1.0

    // Set voice if selected
    if (selectedVoice) {
      const voice = voices.find((v) => v.name === selectedVoice)
      if (voice) {
        utterance.voice = voice
      }
    }

    // Event handlers
    utterance.onstart = () => {
      console.log('[TTS] Chunk started')
      setIsPlaying(true)
    }

    utterance.onend = () => {
      console.log('[TTS] Chunk ended, queue remaining:', textQueueRef.current.length)
      // Speak next chunk after a brief pause
      setTimeout(() => {
        speakNextChunk()
      }, 100)
    }

    utterance.onerror = (error) => {
      console.error('[TTS] Speech synthesis error:', error)
      // On error, continue to next chunk only if it's not an interruption from user action
      if (error.error !== 'interrupted') {
        setTimeout(() => {
          speakNextChunk()
        }, 100)
      }
    }

    utteranceRef.current = utterance
    window.speechSynthesis.speak(utterance)
  }

  const speak = (text: string) => {
    if (!isSupported || !text) return

    console.log(
      `[TTS] speak() called with ${text.length} characters: "${text.substring(0, 100)}..."`,
    )

    // If paused, resume (but this is tricky with chunking, so just restart)
    if (isPaused && currentText === text) {
      window.speechSynthesis.resume()
      setIsPaused(false)
      setIsPlaying(true)
      return
    }

    // Cancel any ongoing speech - be aggressive to prevent overlapping
    const needsCancel = window.speechSynthesis.speaking || window.speechSynthesis.pending
    if (needsCancel) {
      console.log('[TTS] Canceling ongoing speech before starting new')
      window.speechSynthesis.cancel()
    }
    stopKeepAlive()

    // Clean and normalize the text
    const cleanText = text
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()

    if (!cleanText) {
      console.warn('[TTS] No text to speak after cleaning')
      return
    }

    // Helper function to start speaking
    const startSpeaking = () => {
      // Double-check cancel was successful
      if (window.speechSynthesis.speaking) {
        console.log('[TTS] Still speaking, forcing cancel again')
        window.speechSynthesis.cancel()
      }

      // Set states
      setIsPlaying(true)
      setIsPaused(false)
      setCurrentText(text)

      // Chunk the text and start speaking
      textQueueRef.current = chunkText(cleanText)
      console.log(`[TTS] Queued ${textQueueRef.current.length} chunks`)

      // Start keep-alive mechanism
      startKeepAlive()

      // Start speaking first chunk
      speakNextChunk()
    }

    // If we had to cancel, wait a bit before starting new speech
    if (needsCancel) {
      setTimeout(startSpeaking, 100)
    } else {
      startSpeaking()
    }
  }

  const pause = () => {
    if (isPlaying && !isPaused) {
      console.log('[TTS] Pausing')
      window.speechSynthesis.pause()
      stopKeepAlive() // Stop keep-alive when paused
      setIsPaused(true)
      setIsPlaying(false)
    }
  }

  const resume = () => {
    if (isPaused) {
      console.log('[TTS] Resuming')
      window.speechSynthesis.resume()
      startKeepAlive() // Restart keep-alive when resuming
      setIsPaused(false)
      setIsPlaying(true)
    }
  }

  const stop = () => {
    console.log('[TTS] Stopping')
    window.speechSynthesis.cancel()
    stopKeepAlive()
    textQueueRef.current = [] // Clear the queue
    setIsPlaying(false)
    setIsPaused(false)
    setCurrentText('')
  }

  const setRate = (newRate: number) => {
    setRateState(newRate)

    // If currently playing or paused, stop playback
    // User will need to press play to continue with new rate
    if (isPlaying || isPaused) {
      console.log('[TTS] Rate changed while playing/paused - stopping playback')
      stop()
    }
  }

  const setVoice = (voiceName: string) => {
    setSelectedVoice(voiceName)

    // If currently playing or paused, stop playback
    // User will need to press play to continue with new voice
    if (isPlaying || isPaused) {
      console.log('[TTS] Voice changed while playing/paused - stopping playback')
      stop()
    }
  }

  const value: TextToSpeechContextType = {
    isPlaying,
    isPaused,
    rate,
    selectedVoice,
    voices,
    currentText,
    speak,
    pause,
    resume,
    stop,
    setRate,
    setVoice,
    isSupported,
    getQualityVoices,
  }

  return <TextToSpeechContext.Provider value={value}>{children}</TextToSpeechContext.Provider>
}

export function useTextToSpeech() {
  const context = useContext(TextToSpeechContext)
  if (context === undefined) {
    throw new Error('useTextToSpeech must be used within a TextToSpeechProvider')
  }
  return context
}
